import AuthenticatedImage from '../../Items/AuthenticatedImage';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import React from 'react';
import SVG from '../../Images/SvgRenderer';
import { createUseStyles } from 'react-jss';
import { isCypress } from '../../../utils/useCypress';
import { removeDuplicatesJSON } from 'src/utils/useFunctions';
import { useTranslation } from 'react-i18next';
import { useUserData } from 'src/hooks/userDataHook';

interface Props {
  readonly?: any;
  disabled?: any;
  maxRows?: any;
}

const useStyles = createUseStyles((theme: any) => ({
  usersInputWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  usersTopWrapper: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px',
    marginBottom: '7px',
    alignItems: 'center',
    '&:empty': {
      display: 'none',
    },
    '& > label': {
      display: 'flex',
      color: theme.colors.black,
      fontSize: '14px',
      fontWeight: 'bold',
    },
  },
  usersInput: {
    position: 'relative',
    width: 'calc(100% - 22px)',
    maxHeight: (props: Props) => `${(props.maxRows * 42) - 16}px`,
    height: 'fit-content',
    minHeight: '24px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.colors.grey[325],
    borderRadius: '10px',
    backgroundColor: (props: Props) =>
      props.disabled ? theme.colors.grey[75] : theme.colors.white,
    color: (props: Props) =>
      props.disabled ? theme.colors.grey[560] : '',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '8px 10px',
    cursor: (props: Props) =>
      props.readonly || props.disabled ? 'auto' : 'pointer',
    overflow: 'auto',
    gap: '14px',
    '&.disabled': {
      backgroundColor: theme.colors.grey[75],
      color: theme.colors.grey[560],
      cursor: 'auto',
    },
    '& > span': {
      fontSize: '14px',
    },
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px',
    height: '32px',
    backgroundColor: '#E1F1FF !important',
    color: theme.colors.primaryBlue[500] + ' !important',
    fontWeight: 'bold',
    position: 'relative',
    cursor: 'pointer',
    gap: '5px',
    '& > span': {
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingLeft: '2px',
    },
    '& > div': {
      width: '18px',
      height: '18px',
      borderRadius: '6px',
      backgroundColor: theme.colors.white,
      position: 'relative',
    },
    '& > svg': {
      width: '20px',
      height: '20px',
    },
  },
  deleteIcon: {
    position: 'absolute',
    top: '50%',
    right: '-16px',
    transform: 'translateY(-50%)',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: theme.colors.white,
    borderRadius: '100%',
    backgroundColor: theme.colors.primaryBlue[500],
    color: theme.colors.white,
    transition: 'background-color 0.25s',
    width: '22px',
    height: '22px',
    minWidth: '22px',
    minHeight: '22px',
    maxWidth: '22px',
    maxHeight: '22px',
    padding: '0',
    '&:hover': {
      backgroundColor: theme.colors.primaryBlue[600],
    },
    '& > svg': {
      width: '10px',
      height: '10px',
    },
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.grey[560],
    fontSize: '14px',
    whiteSpace: 'break-spaces',
    overflowWrap: 'break-word',
    '& > svg': {
      width: '20px',
      height: '20px',
      marginRight: '8px',
      color: 'inherit',
    },
  },
  usersCount: {
    backgroundColor: theme.colors.grey[250],
    borderRadius: '20px',
    padding: '2px 5px',
    display: 'flex',
    alignItems: 'center',
    height: 'fit-content',
    '& > svg': {
      color: theme.colors.primaryBlue[500],
      width: '14px',
      height: '14px',
    },
    '& > span': {
      marginBottom: '0',
      marginLeft: '5px',
      color: theme.colors.primaryBlue[500],
      fontWeight: 'bold',
      fontSize: '14px',
    },
  },
}));

type InputType = {
  label?: any;
  showSelectLabel?: boolean;
  showSelectLabelIcon?: boolean;
  selectLabel?: any;
  selectedUsers: any;
  setSelectedUsers: any;
  isShowUsersCount?: any;
  isDeletable?: boolean;
  dataCy?: any;
  onClick?: any;
  disabled?: boolean;
  readonly?: boolean;
  maxRows?: number;
  className?: any;
  classNameWrapper?: any;
  classNameChip?: any;
};

const UserChip: React.FC<{
  userID: number;
  onDelete: () => void;
  disabled: boolean;
  readonly: boolean;
  isDeletable: boolean;
  classNameChip?: string;
  classes: ReturnType<typeof useStyles>;
}> = ({ userID, onDelete, disabled, readonly, isDeletable, classNameChip, classes }) => {
  const userData = useUserData(userID);

  return (
    <Chip
      className={`${classes.chip} ${classNameChip ?? ''}`}
      size="small"
      key={`user_${userID}`}
      icon={<AuthenticatedImage thumbLink={userData.photo?.thumbLink} />}
      label={userData.firstname + ' ' + userData.surname}
      onDelete={() => onDelete()}
      color="primary"
      disabled={disabled}
      deleteIcon={
        !disabled && !readonly && isDeletable ? (
          <Button className={classes.deleteIcon}>
            <SVG src="close" />
          </Button>
        ) : undefined
      }
    />
  );
};

const UsersInput: React.FC<InputType> = ({
  label,
  showSelectLabel = true,
  showSelectLabelIcon = true,
  selectLabel,
  selectedUsers,
  setSelectedUsers,
  isShowUsersCount,
  isDeletable = true,
  onClick,
  disabled = false,
  readonly = false,
  maxRows = 1,
  className,
  classNameWrapper,
  classNameChip,
}) => {
  const classes = useStyles({ readonly, disabled, maxRows });
  const { t } = useTranslation();

  const uniqueUsers = removeDuplicatesJSON(selectedUsers.map((u: any) => ({ userID: u.userID })));

  const handleDelete = (userID: number) => {
    const newUsers = selectedUsers.filter((u: any) => u.userID !== userID);
    setSelectedUsers(newUsers);
  };

  return (
    <div className={`${classes.usersInputWrapper} ${classNameWrapper ?? ''}`}>
      <div className={classes.usersTopWrapper}>
        {label && <label>{label}</label>}
        {isShowUsersCount !== undefined && (
          <span className={classes.usersCount}>
            <SVG src="users" />
            <span data-clarity-unmask="true">
              {isShowUsersCount === 'unique' ? uniqueUsers.length : selectedUsers.length}
            </span>
          </span>
        )}
      </div>

      <div
        className={`${classes.usersInput} ${className ?? ''}`}
        onClick={disabled || readonly ? undefined : onClick}
        data-cy={isCypress() ? 'employeesInput' : undefined}
      >
        {uniqueUsers.length > 0 ? (
          uniqueUsers.map((user: any) => (
            <UserChip
              key={user.userID}
              userID={user.userID}
              onDelete={() => handleDelete(user.userID)}
              disabled={disabled}
              readonly={readonly}
              isDeletable={isDeletable}
              classNameChip={classNameChip}
              classes={classes}
            />
          ))
        ) : (
          showSelectLabel && (
            <span className={classes.placeholder}>
              {showSelectLabelIcon && <SVG src="users" />}
              {selectLabel ?? t('select_users')}
            </span>
          )
        )}
      </div>
    </div>
  );
};

export default UsersInput;